import type { FileWithMeta } from '@risksmart-app/components/src/file/types';
import { CustomForm } from '@risksmart-app/components/src/form-builder/CustomForm';
import {
  parseQuestionnaireFormSchema,
  parseQuestionnaireFormUiSchema,
  parseQuestionnaireResponseData,
} from '@risksmart-app/components/src/form-builder/questionnaireSchemaContracts';
import {
  defaultSchema,
  defaultUISchema,
} from '@risksmart-app/components/src/form-builder/store/useFormBuilderStore';
import type { ResponseData } from '@risksmart-app/components/src/form-builder/types';
import Loading from '@risksmart-app/components/src/loading';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import {
  RESPONDENT_LOCKED_STATUSES,
  ThirdPartyResponseStatus as Third_Party_Response_Status_Enum,
} from '@risksmart-app/domain/src/types/consts/third-party-response-status';
import { isEmpty } from 'lodash';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { LoadFailureContent } from 'src/components/LoadFailureContent';
import { useFileUpdate } from 'src/hooks/useFileUpdate';
import { useTppResponseById } from 'src/hooks/useTppResponseById';
import { useTppUpdateResponse } from 'src/hooks/useTppUpdateResponse';
import { PageLayout } from 'src/layouts';

import { enrichFilesWithPathMetadata, removeDeletedFiles } from './fileUtils';
import { toResponseDataJsonb } from './responseData';

const Page: FC = () => {
  const Id = useGetGuidParam('id');
  const { t } = useTranslation(['common'], {
    keyPrefix: 'third_party_responses.questionnaire_form.notification',
  });

  const notificationI18n = {
    saveSuccess: t('save_success'),
    submitSuccess: t('submit_success'),
    submitError: t('submit_error'),
  };

  const navigate = useNavigate();
  const update = useTppUpdateResponse();
  const { data, loading, error } = useTppResponseById(Id);
  const { updateFiles } = useFileUpdate();

  const responseData = data?.third_party_response_by_pk;
  const originalFiles = responseData?.files;
  // Shares RESPONDENT_LOCKED_STATUSES with the two server-side guards
  // (answer-save + attachment-detach) so the form can never render editable for
  // a status the data-layer will reject. This previously listed the statuses
  // inline and omitted `expired`, so an expired questionnaire rendered as an
  // editable form and the respondent only discovered it was frozen when the
  // save came back 409 — losing everything they had typed.
  const readOnly =
    !!responseData?.Status &&
    RESPONDENT_LOCKED_STATUSES.includes(responseData.Status);

  if (loading) {
    return (
      <PageLayout>
        <Loading />
      </PageLayout>
    );
  }

  // Must precede the schema check below: on a failed read `data` is undefined,
  // so that check throws `Schema or UISchema is empty` and blames the template
  // for what was a fetch failure.
  if (error) {
    return (
      <PageLayout>
        <LoadFailureContent />
      </PageLayout>
    );
  }

  if (
    isEmpty(
      data?.third_party_response_by_pk?.questionnaireTemplateVersion?.Schema
    ) ||
    isEmpty(
      data?.third_party_response_by_pk?.questionnaireTemplateVersion?.UISchema
    )
  ) {
    throw new Error('Schema or UISchema is empty');
  }

  const schema = parseQuestionnaireFormSchema(
    responseData?.questionnaireTemplateVersion?.Schema,
    defaultSchema
  );
  const uiSchema = parseQuestionnaireFormUiSchema(
    responseData?.questionnaireTemplateVersion?.UISchema,
    defaultUISchema
  );
  const values = {
    ...parseQuestionnaireResponseData(responseData?.ResponseData),
    files: (responseData?.files ?? []).flatMap(({ file, ...relation }) =>
      file ? [{ ...relation, file }] : []
    ),
  };

  const onSave =
    (
      status:
        | typeof Third_Party_Response_Status_Enum.InProgress
        | typeof Third_Party_Response_Status_Enum.AwaitingReview
    ) =>
    async (data: ResponseData) => {
      // Attachment maps are only present after file interaction. ResponseData
      // permits both map shapes but cannot distinguish them by property name.
      const newFiles = (data.newFiles ?? {}) as { [key: string]: File[] }; // ResponseData index signature doesn't distinguish field shapes — newFiles is always File[] values
      // updatedFiles contains FileWithMeta[] values; ResponseData index signature doesn't distinguish field shapes
      const typedUpdatedFiles = (data.updatedFiles ?? {}) as {
        [key: string]: FileWithMeta[];
      };

      // Files BEFORE the status change (COI-1120): submit moves the response to
      // a RESPONDENT_LOCKED_STATUS, and every respondent file write is gated on
      // it still being editable, so updating first 403s the attachment.
      await updateFiles({
        parentId: Id,
        parentType: Parent_Type_Enum.ThirdPartyResponse,
        originalFiles: originalFiles?.map((f) => f.file),
        selectedFiles: [
          ...enrichFilesWithPathMetadata(newFiles),
          ...removeDeletedFiles(originalFiles, typedUpdatedFiles),
        ],
      });

      await update({
        id: Id,
        responseData: toResponseDataJsonb(data),
        status,
      });

      navigate('/');
    };

  return (
    <PageLayout
      title={`${responseData?.questionnaireTemplateVersion?.parent?.Title}`}
      counter={`(v${responseData?.questionnaireTemplateVersion?.Version})`}
    >
      <CustomForm
        onCancel={() => navigate('..')}
        onSubmit={onSave(Third_Party_Response_Status_Enum.AwaitingReview)}
        onSave={onSave(Third_Party_Response_Status_Enum.InProgress)}
        values={values}
        schema={schema}
        uischema={uiSchema}
        notificationI18n={notificationI18n}
        readOnly={readOnly}
      />
    </PageLayout>
  );
};

export default Page;
